import React, { useMemo } from 'react'
import { Doughnut } from 'react-chartjs-2'
import { useTranslation } from 'react-i18next'
import styles from './results-doughnut-chart.module.scss'
import { KeyProps, DoughnutChartProps, GameType } from './ResultsDoughnutChartTypes'
import { globalTooltipConfig } from '../ChartsGlobalConfiguration'

const keyColours = [styles.wins, styles.losses, styles.walkovers, styles.retirements]

const Chart = require('react-chartjs-2').Chart //Definition is missing from library for Chart

//Doughnut chart does not let you enter text in centre without modifying Chart
const originalDoughnutDraw = Chart.controllers.doughnut.prototype.draw
Chart.helpers.extend(Chart.controllers.doughnut.prototype, {
  draw: function () {
    originalDoughnutDraw.apply(this, arguments)

    const chart = this.chart.chart
    const ctx = chart.ctx
    const width = chart.width
    const height = chart.height

    const fontSize = (height / 120).toFixed(2)
    ctx.font = 'bold ' + fontSize + 'em Poppins'
    ctx.fillStyle = '#16234C'
    ctx.textBaseline = 'middle'

    let text = chart.config.data.text
    let textX = Math.round((width - ctx.measureText(text).width) / 2)
    let textY = height / 2 - 10

    if (text) {
      ctx.fillText(text, textX, textY)
      ctx.font = 'bold ' + (height / 300).toFixed(2) + 'em Poppins'
    }
    text = chart.config.data.subtext
    if (text) {
      textX = Math.round((width - ctx.measureText(text).width) / 2)
      textY += (height / 220) * 25
      ctx.fillText(text, textX, textY)
    }
  },
})

function calcTotal(data: number[]) {
  return data.reduce((a: number, b: any) => a + b, 0)
}

function mapResultsToKey(object, callback) {
  return Object.keys(object).map(function (key, index) {
    return callback(key, object[key], index)
  })
}

function calcResultPercentages(data: number[]) {
  const total = calcTotal(data)
  return data.map((num: number) => Math.round((num / total) * 100) || 0)
}

const ResultsDoughnutChartKey: React.FC<KeyProps> = ({ data }) => {
  const { t } = useTranslation('strCharts')

  const keys = {
    matchesWon: t('wins'),
    matchesLost: t('losses'),
  }

  const chartData = Object.values(data)
  chartData.shift()
  const percentages = calcResultPercentages(chartData)
  return (
    <ul
      className={`${styles.resultsDoughnutChartKeys} results-doughnut-chart_resultsDoughnutChartKeys--override`}
    >
      {mapResultsToKey(data, (key, value, index) => {
        return (
          key !== 'totalMatches' && (
            <li key={key} className={styles.chartKey + ' ' + keyColours[index - 1]}>
              <span className={styles.chartKeyText}>
                {value ?? 0} {keys[key]}
              </span>
              <span className={styles.chartKeyStat}> ({percentages[index - 1]}%)</span>
            </li>
          )
        )
      })}
    </ul>
  )
}

const ResultsDoughnutChart: React.FC<DoughnutChartProps> = ({ data, type }) => {
  const { t } = useTranslation('strCharts')

  const chartData = useMemo(() => {
    const typeData = data[type.toLowerCase()] || {}
    const { totalMatches, ...winsAndLosses } = typeData

    return {
      labels: [t('wins'), t('losses'), 'Walkovers', 'Retirements'],
      datasets: [
        {
          data: Object.values(winsAndLosses),
          backgroundColor: ['#E53E51', '#2A3B6B', '#f0f0f0', '#808080'],
          hoverBackgroundColor: ['#E53E51', '#2A3B6B', '#f0f0f0', '#808080'],
        },
      ],
      text: `${totalMatches || 0}`,
      subtext: 'Total matches',
    }
  }, [data, type, t])

  return (
    <div
      className={`${styles.doughnutChartKeyGroup} results-doughnut-chart_doughnutChartKeyGroup--override`}
    >
      <div>
        <Doughnut
          data={chartData}
          height={320}
          options={{
            elements: {
              arc: {
                borderWidth: 0,
              },
            },
            cutoutPercentage: 76,
            legend: {
              display: false,
            },
            tooltips: globalTooltipConfig,
          }}
        />
      </div>
      <div>
        <ResultsDoughnutChartKey data={data[type.toLocaleLowerCase()] || {}} />
      </div>
    </div>
  )
}

export default ResultsDoughnutChart
